import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { apiFetch } from "../api";
import SettingsModal from "./SettingsModal";

// Lightweight inline SVG icons — keeps the bundle small and avoids forcing
// a particular icon-set choice. Each accepts standard SVG props.
// ID-card-with-person — used for the "Profiles" entry (the bureau's
// matrimonial profile roster). Card frame on the outside, single person
// silhouette on the left, two short text lines on the right (representing
// the profile's fields). Reads as "an entry in the profile directory."
const ProfilesIcon = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <circle cx="8.5" cy="11" r="2" />
    <path d="M5 17c0-1.7 1.6-3 3.5-3s3.5 1.3 3.5 3" />
    <line x1="14.5" y1="10" x2="19" y2="10" />
    <line x1="14.5" y1="14" x2="18" y2="14" />
  </svg>
);
// Person + settings cog — industry-standard "user management" pattern
// (GitHub, AWS admin, etc.). Distinguishes Client Management (the screen
// where the bureau acts on a specific client — invite, reset password,
// set match prefs, follow up) from the broader Profiles browse view.
const ClientManagementIcon = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
    {/* Person silhouette */}
    <circle cx="10" cy="7" r="4" />
    <path d="M2 21v-2a4 4 0 0 1 4-4h6a4 4 0 0 1 4 4" />
    {/* Settings cog in the top-right corner */}
    <circle cx="19" cy="6" r="2" />
    <line x1="19" y1="2.5" x2="19" y2="4" />
    <line x1="19" y1="8" x2="19" y2="9.5" />
    <line x1="15.5" y1="6" x2="17" y2="6" />
    <line x1="21" y1="6" x2="22.5" y2="6" />
    <line x1="16.6" y1="3.6" x2="17.6" y2="4.6" />
    <line x1="20.4" y1="7.4" x2="21.4" y2="8.4" />
    <line x1="16.6" y1="8.4" x2="17.6" y2="7.4" />
    <line x1="20.4" y1="4.6" x2="21.4" y2="3.6" />
  </svg>
);
// Three-person huddle — depicts the bureau's "team" (owner + admins +
// assistants). One person centered and forward, two flanking and slightly
// behind, so the silhouette reads as a group rather than a couple.
const TeamIcon = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
    {/* back-left */}
    <circle cx="5" cy="7.5" r="1.8" />
    <path d="M1 14c0-2 1.6-3.5 4-3.5" />
    {/* back-right */}
    <circle cx="19" cy="7.5" r="1.8" />
    <path d="M23 14c0-2-1.6-3.5-4-3.5" />
    {/* front center */}
    <circle cx="12" cy="9" r="2.5" />
    <path d="M6 21v-1a6 6 0 0 1 12 0v1" />
  </svg>
);
const ListIcon = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
    <line x1="8" y1="6" x2="21" y2="6" />
    <line x1="8" y1="12" x2="21" y2="12" />
    <line x1="8" y1="18" x2="21" y2="18" />
    <line x1="3" y1="6" x2="3.01" y2="6" />
    <line x1="3" y1="12" x2="3.01" y2="12" />
    <line x1="3" y1="18" x2="3.01" y2="18" />
  </svg>
);
const HeartIcon = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);
// Two person silhouettes with a filled heart between them — "two
// matched profiles". Used for Matches Tracking so the icon explicitly
// reads as paired matches rather than a generic list.
const MatchTrackingIcon = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
    {/* Left person */}
    <circle cx="6" cy="11" r="2.2" />
    <path d="M2 20v-1a4 4 0 0 1 4-4 4 4 0 0 1 4 4v1" />
    {/* Right person */}
    <circle cx="18" cy="11" r="2.2" />
    <path d="M14 20v-1a4 4 0 0 1 4-4 4 4 0 0 1 4 4v1" />
    {/* Filled heart connecting them at top center */}
    <path
      d="M12 8.5l-2.4-2.4a1.5 1.5 0 0 1 0-2.2 1.5 1.5 0 0 1 2.1 0L12 4l.3-.1a1.5 1.5 0 0 1 2.1 0 1.5 1.5 0 0 1 0 2.2L12 8.5z"
      fill="currentColor"
      stroke="none"
    />
  </svg>
);
// Person silhouette with an X to its right — the standard "user-x" /
// disabled-user pattern. Used for Deactivated Profiles so the icon reads
// as a person who's been turned off rather than a generic archive box.
const DeactivatedProfileIcon = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
    {/* Person silhouette on the left */}
    <circle cx="9" cy="7" r="4" />
    <path d="M1 21v-2a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v2" />
    {/* X mark to the right */}
    <line x1="17" y1="8" x2="22" y2="13" />
    <line x1="22" y1="8" x2="17" y2="13" />
  </svg>
);
// "Love letter" — envelope with a filled heart inside. Used for the
// bureau's incoming Interest Requests inbox so the icon reads as
// matrimonial-interest messages rather than generic mail.
const InterestRequestIcon = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
    {/* Sealed envelope outline */}
    <rect x="3" y="5" width="18" height="14" rx="2" />
    {/* Heart centered on the envelope, filled so it stands out */}
    <path
      d="M12 16.2l-3.6-3.6a2.2 2.2 0 0 1 0-3.1 2.2 2.2 0 0 1 3.1 0L12 9.9l.5-.4a2.2 2.2 0 0 1 3.1 0 2.2 2.2 0 0 1 0 3.1L12 16.2z"
      fill="currentColor"
      stroke="none"
    />
  </svg>
);
// Building silhouette for the bureau directory (browse all bureaus).
const BuildingIcon = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
    <path d="M3 21h18" />
    <path d="M5 21V7l8-4v18" />
    <path d="M19 21V11l-6-4" />
    <line x1="9" y1="9" x2="9" y2="9.01" />
    <line x1="9" y1="12" x2="9" y2="12.01" />
    <line x1="9" y1="15" x2="9" y2="15.01" />
    <line x1="9" y1="18" x2="9" y2="18.01" />
  </svg>
);
// Award/badge ribbon — represents the seeker's active relationship with
// a single bureau (their "home" bureau).
const BadgeIcon = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
    <circle cx="12" cy="9" r="6" />
    <polyline points="8.21 13.89 7 22 12 19 17 22 15.79 13.88" />
  </svg>
);
// Person-with-plus for "people requesting to join the bureau" (bureau-side
// association requests inbox).
const UserPlusIcon = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
    <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
    <circle cx="8.5" cy="7" r="4" />
    <line x1="20" y1="8" x2="20" y2="14" />
    <line x1="23" y1="11" x2="17" y2="11" />
  </svg>
);
const SearchIcon = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
    <circle cx="11" cy="11" r="7" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const SettingsIcon = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const SIDEBAR_COLLAPSED_KEY = "sidebarCollapsed";

// Module-level singleton poller for the two unread-count endpoints
// (interest requests + association requests). Both Sidebar and NavBar's
// mobile menu subscribe to the same source, so:
//   - Polling fires once per minute total, not per-hook-instance.
//   - Navigations don't tear-down/re-fire the interval (the previous
//     `location.pathname` dep was causing 2 extra calls on every click,
//     which combined with the global 100/min rate limit was tripping
//     429s during normal usage).
//   - Mark-seen events dispatched by inbox pages reset both subscribers
//     instantly without an extra fetch.
const __counts = { interests: 0, associations: 0 };
const __subs = new Set();
let __pollHandle = null;
let __mode = null; // "public" | "bureau"

const emitToSubs = () => {
  for (const fn of __subs) fn({ ...__counts });
};

const startPolling = (isPublicUser) => {
  const interestsEndpoint = isPublicUser
    ? "/public/interests/unread-count"
    : "/interests/unread-count";
  const associationsEndpoint = isPublicUser
    ? "/public/bureau-associations/unread-count"
    : "/bureau-associations/unread-count";
  const tick = () => {
    apiFetch(interestsEndpoint)
      .then((r) => {
        __counts.interests = r?.count || 0;
        emitToSubs();
      })
      .catch(() => {});
    apiFetch(associationsEndpoint)
      .then((r) => {
        __counts.associations = r?.count || 0;
        emitToSubs();
      })
      .catch(() => {});
  };
  tick();
  __pollHandle = setInterval(tick, 60000);

  // Mark-seen events from inbox pages reset both counters immediately
  // without firing another fetch.
  const onInterestsSeen = () => {
    __counts.interests = 0;
    emitToSubs();
  };
  const onAssociationsSeen = () => {
    __counts.associations = 0;
    emitToSubs();
  };
  window.addEventListener("interests:seen", onInterestsSeen);
  window.addEventListener("associations:seen", onAssociationsSeen);

  return () => {
    clearInterval(__pollHandle);
    __pollHandle = null;
    window.removeEventListener("interests:seen", onInterestsSeen);
    window.removeEventListener("associations:seen", onAssociationsSeen);
  };
};

let __stopPolling = null;

const ensurePolling = (isPublicUser) => {
  const nextMode = isPublicUser ? "public" : "bureau";
  if (__mode === nextMode && __pollHandle) return; // already running
  if (__stopPolling) __stopPolling();
  __mode = nextMode;
  __stopPolling = startPolling(isPublicUser);
};

const stopPollingIfIdle = () => {
  if (__subs.size === 0 && __stopPolling) {
    __stopPolling();
    __stopPolling = null;
    __mode = null;
    __counts.interests = 0;
    __counts.associations = 0;
  }
};

export function useUnreadCounts({ isPublicUser, enabled = true } = {}) {
  const [snapshot, setSnapshot] = useState(__counts);

  useEffect(() => {
    if (!enabled) return undefined;
    setSnapshot({ ...__counts });
    const sub = (next) => setSnapshot(next);
    __subs.add(sub);
    ensurePolling(isPublicUser);
    return () => {
      __subs.delete(sub);
      stopPollingIfIdle();
    };
  }, [isPublicUser, enabled]);

  return {
    unreadInterests: snapshot.interests,
    unreadAssociations: snapshot.associations,
  };
}

// Single source of truth for primary navigation. Both the desktop Sidebar
// and the NavBar mobile-menu drawer render from this list, so the two can
// never drift apart on label, path, or icon.
export function buildNavItems({
  isPublicUser,
  unreadInterests = 0,
  unreadAssociations = 0,
}) {
  return isPublicUser
    ? [
        { label: "Find Matches", path: "/find-matches", Icon: SearchIcon },
        // Distinct icons so seekers can tell Bureaus (directory) apart
        // from My Bureau (the one they're associated with) at a glance.
        { label: "Bureaus", path: "/bureaus", Icon: BuildingIcon },
        {
          label: "My Bureau",
          path: "/my-bureau",
          Icon: BadgeIcon,
          badge: unreadAssociations,
        },
        {
          label: "My Interests",
          path: "/my-interests",
          Icon: HeartIcon,
          badge: unreadInterests,
        },
        { label: "My Matches", path: "/my-referrals", Icon: ListIcon },
      ]
    : [
        { label: "Profiles", path: "/bureau/dashboard", Icon: ProfilesIcon },
        { label: "Client Management", path: "/bureau/clients", Icon: ClientManagementIcon },
        { label: "Matches Tracking", path: "/bureau/referrals", Icon: MatchTrackingIcon },
        {
          label: "Interest Requests",
          path: "/bureau/interest-requests",
          Icon: InterestRequestIcon,
          badge: unreadInterests,
        },
        // UserPlusIcon for "incoming people wanting to join the bureau" —
        // semantically separates this inbox from interest requests above.
        {
          label: "Association Requests",
          path: "/bureau/association-requests",
          Icon: UserPlusIcon,
          badge: unreadAssociations,
        },
        { label: "Bureau Team", path: "/bureau/members", Icon: TeamIcon },
        { label: "Deactivated", path: "/bureau/deactivated", Icon: DeactivatedProfileIcon },
      ];
}

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isRTL } = useLanguage();

  // Sidebar is always physically collapsed (so the page-content shift is
  // constant — no layout jitter); hovering it visually expands the rail
  // as an overlay above the page content.
  const [hovered, setHovered] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const expanded = hovered;

  const isPublicUser =
    typeof window !== "undefined" &&
    localStorage.getItem("publicUserToken") !== null;

  const { unreadInterests, unreadAssociations } = useUnreadCounts({
    isPublicUser,
  });

  const items = buildNavItems({
    isPublicUser,
    unreadInterests,
    unreadAssociations,
  });

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  return (
    <aside
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`hidden md:flex fixed top-0 ${
        isRTL ? "right-0 border-l" : "left-0 border-r"
      } h-screen flex-col bg-[var(--color-surface)] border-[var(--color-border)] transition-[width] duration-200 z-50 ${
        expanded ? "w-60 shadow-xl" : "w-16"
      }`}
      aria-label="Primary navigation"
    >
      {/* Brand header — height matches the NavBar (~64px) when collapsed,
          grows to fit the tagline below the wordmark when expanded. */}
      <div
        className={`flex items-center border-b border-[var(--color-border)] ${
          expanded ? "py-2 px-3" : "h-16 justify-center px-2"
        }`}
      >
        <button
          type="button"
          onClick={() =>
            navigate(isPublicUser ? "/my-referrals" : "/bureau/dashboard")
          }
          title="Phuphoo home"
          className={`group w-full flex items-center gap-2 min-w-0 rounded-lg py-1 text-[var(--color-text-strong)] hover:bg-[var(--color-primary)] hover:text-[var(--color-on-primary)] transition-colors ${
            expanded ? "px-2 justify-start" : "justify-center px-1"
          }`}
        >
          <span
            className="w-8 h-8 shrink-0 rounded-lg bg-[var(--color-primary)] text-[var(--color-on-primary)] flex items-center justify-center font-bold text-sm shadow-sm"
            aria-hidden="true"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
              <path d="M12 21s-7-4.5-9.5-9A5.5 5.5 0 0 1 12 6a5.5 5.5 0 0 1 9.5 6c-2.5 4.5-9.5 9-9.5 9z" />
            </svg>
          </span>
          {expanded && (
            <span className="flex flex-col items-start min-w-0 leading-tight">
            <span
              className="font-bold text-base truncate flex items-center gap-[2px]"
              aria-label="Phuphoo"
            >
              <span>Phuph</span>
              {/* Stylised "oo" — each o is a cartoon eye:
                    - vertical ellipse outline
                    - small brow flick at top-right
                    - off-centre pupil (filled, bottom-left of the eye)
                    - small highlight dot (white in default state, primary
                      on hover so it stays visible against the white pupil) */}
              {[0, 1].map((i) => (
                <svg
                  key={i}
                  viewBox="0 0 16 20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-[14px] h-[18px] inline-block shrink-0"
                  aria-hidden="true"
                >
                  {/* eye outline (vertical oval) */}
                  <ellipse cx="8" cy="11" rx="6.5" ry="8" />
                  {/* brow flick top-right */}
                  <path d="M 11 2.5 Q 13 1 14.5 3" />
                  {/* pupil (off-centre) */}
                  <circle cx="6.5" cy="12" r="3.6" fill="currentColor" stroke="none" />
                  {/* highlight inside pupil */}
                  <circle
                    cx="5"
                    cy="10.5"
                    r="1.1"
                    stroke="none"
                    className="fill-[var(--color-surface)] group-hover:fill-[var(--color-primary)]"
                  />
                </svg>
              ))}
            </span>
            <span className="text-[10px] font-medium opacity-70 italic truncate mt-0.5">
              Rakhay sub par nazar
            </span>
            </span>
          )}
        </button>
      </div>

      <nav className="flex-1 py-3 space-y-1 overflow-y-auto overflow-x-hidden min-h-0">
        {items.map(({ label, path, Icon, badge }) => {
          const active = isActive(path);
          const showBadge = typeof badge === "number" && badge > 0;
          return (
            <button
              key={path}
              type="button"
              onClick={() => navigate(path)}
              title={
                !expanded
                  ? showBadge
                    ? `${label} (${badge})`
                    : label
                  : undefined
              }
              aria-current={active ? "page" : undefined}
              className={`relative w-[calc(100%-1rem)] mx-2 flex items-center gap-3 ${
                expanded ? "px-4" : "justify-center"
              } py-2 rounded-lg text-sm font-medium transition-colors ${
                active
                  ? "bg-[var(--color-primary)] text-[var(--color-on-primary)] font-semibold shadow-sm"
                  : "text-[var(--color-text)] hover:bg-[var(--color-surface-hover)]"
              }`}
            >
              <span className="relative shrink-0">
                <Icon className="w-5 h-5" />
                {/* Collapsed rail: badge as a corner dot. */}
                {showBadge && !expanded && (
                  <span className="absolute -top-1 -right-1 min-w-[1rem] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold leading-4 text-center">
                    {badge > 9 ? "9+" : badge}
                  </span>
                )}
              </span>
              {expanded && (
                <>
                  <span className="truncate flex-1 text-left">{label}</span>
                  {showBadge && (
                    <span className="min-w-[1.25rem] h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-semibold leading-5 text-center">
                      {badge > 99 ? "99+" : badge}
                    </span>
                  )}
                </>
              )}
            </button>
          );
        })}
      </nav>

      {/* Settings — pinned to the bottom of the rail. Same styling as the
          nav items above for visual consistency. Opens a modal with the
          language and theme controls. */}
      <button
        type="button"
        onClick={() => setSettingsOpen(true)}
        title={!expanded ? "Settings" : undefined}
        className={`w-[calc(100%-1rem)] mx-2 mb-3 flex items-center gap-3 ${
          expanded ? "px-4" : "justify-center"
        } py-2 rounded-lg text-sm font-medium transition-colors text-[var(--color-text)] hover:bg-[var(--color-surface-hover)]`}
      >
        <SettingsIcon className="w-5 h-5 shrink-0" />
        {expanded && <span className="truncate">Settings</span>}
      </button>

      <SettingsModal
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />
    </aside>
  );
};

export { SIDEBAR_COLLAPSED_KEY };
export default Sidebar;
